import { useState } from 'react';
import { useApp } from '@context/AppContext';
import { useRelay } from './useRelay';
import GameLobby from '../shared/GameLobby';
import themeData from '@/data/themeSeeds.json';
import { RefreshCw, Eye, EyeOff } from 'lucide-react';

const TIMER_OPTIONS = [
  { label: '10s', value: 10 },
  { label: '20s', value: 20 },
  { label: '30s', value: 30 },
];

const ROUND_OPTIONS = [1, 2, 3];

export default function RelayLobby() {
  const { state } = useApp();
  const { game, user } = state;
  const { initRelay, startRelay, endGame } = useRelay();

  const [timerDuration, setTimerDuration] = useState(20);
  const [totalRounds, setTotalRounds] = useState(2);
  const [theme, setTheme] = useState('');
  const [blindRelay, setBlindRelay] = useState(false);
  const [randomOrder, setRandomOrder] = useState(true);
  const [initialised, setInitialised] = useState(false);

  const handleInit = () => {
    initRelay({ timerDuration, totalRounds, theme: theme || null, blindRelay, randomOrder });
    setInitialised(true);
  };

  const handleRandomTheme = () => {
    const seeds = themeData.themeSeeds;
    setTheme(seeds[Math.floor(Math.random() * seeds.length)]);
  };

  const memberCount = Object.keys(state.room.members || {}).length;
  const totalTurns = memberCount * totalRounds;

  return (
    <GameLobby
      title="Relay Mode"
      emoji="🔄"
      onStart={initialised ? startRelay : handleInit}
      onCancel={endGame}
      startLabel={initialised ? 'Start Relay!' : 'Set Up'}
      startDisabled={false}
    >
      {user.isHost ? (
        <div className="space-y-4">
          {/* Turn duration */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
              Turn Duration
            </p>
            <div className="flex gap-2">
              {TIMER_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => setTimerDuration(value)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-display transition-all ${
                    timerDuration === value
                      ? 'bg-amber text-white font-semibold'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Rotations */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
              Rotations ({totalTurns} turns total)
            </p>
            <div className="flex gap-2">
              {ROUND_OPTIONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setTotalRounds(r)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-display transition-all ${
                    totalRounds === r
                      ? 'bg-amber text-white font-semibold'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {r}×
                </button>
              ))}
            </div>
          </div>

          {/* Theme (optional) */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
              Theme (optional)
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                placeholder="e.g. A day at the beach"
                maxLength={100}
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm font-display text-charcoal placeholder:text-slate-300 focus:outline-none focus:border-amber/50"
              />
              <button
                onClick={handleRandomTheme}
                title="Random theme"
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-400 hover:text-amber transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Blind relay toggle */}
          <button
            onClick={() => setBlindRelay(!blindRelay)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
              blindRelay
                ? 'border-violet-300 bg-violet-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            {blindRelay ? (
              <EyeOff className="w-5 h-5 text-violet-500" />
            ) : (
              <Eye className="w-5 h-5 text-slate-400" />
            )}
            <div className="text-left">
              <p className="text-sm font-medium text-charcoal font-display">
                Blind Relay {blindRelay ? '(On)' : '(Off)'}
              </p>
              <p className="text-xs text-slate-400">
                {blindRelay
                  ? "Drawers only see the edges of previous work — big reveal at the end!"
                  : 'Drawers see the full canvas from previous turns'}
              </p>
            </div>
          </button>

          {/* Turn order */}
          <button
            onClick={() => setRandomOrder(!randomOrder)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors"
          >
            <span className="text-lg">{randomOrder ? '🎲' : '📋'}</span>
            <div className="text-left">
              <p className="text-sm font-medium text-charcoal font-display">
                Turn Order: {randomOrder ? 'Random' : 'Join Order'}
              </p>
            </div>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {game.theme && (
            <div className="bg-amber/5 border border-amber/20 rounded-xl p-3">
              <p className="text-xs text-amber font-display uppercase tracking-wide mb-1">Theme</p>
              <p className="text-sm font-semibold text-charcoal font-display">{game.theme}</p>
            </div>
          )}
          {game.blindRelay && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
              <p className="text-sm text-violet-600 font-display flex items-center gap-2">
                <EyeOff className="w-4 h-4" />
                Blind Relay — you won&apos;t see the full canvas until the end!
              </p>
            </div>
          )}
          <p className="text-sm text-slate-400 font-display text-center">
            Waiting for host to start...
          </p>
        </div>
      )}
    </GameLobby>
  );
}
